/**
 * Registration selector derivation.
 *
 * The selector is a one-way, length-framed, domain-separated SHA-256 digest of
 * the session root, a per-registration nonce, and the TTL:
 *
 *   selector = SHA-256(
 *     UTF8("fume.selector.v1")
 *     || uint64be(R.length)     || R
 *     || uint64be(nonce.length) || nonce
 *     || uint64be(8)            || uint64be(ttlSeconds)
 *   )
 *
 * The domain separator plus `uint64be(length) || bytes` framing make the
 * pre-image unambiguous, so no two distinct (R, nonce, ttl) triples can collide
 * by field-boundary confusion. Because it is a hash of `R`, the selector never
 * reveals `R`.
 */
import { sha256 } from './hash.js';
import { FumeCryptoError } from './errors.js';
import { SELECTOR_DOMAIN, SELECTOR_NONCE_SIZE, SESSION_ROOT_SIZE } from './constants.js';
import { concatBytes, requireLength, uint64be } from './internal.js';
const utf8 = new TextEncoder();
const MAX_TTL_SECONDS = 2 ** 32 - 1;
/**
 * Derive the 32-byte registration selector from `R`, `nonce`, and `ttlSeconds`.
 *
 * @throws {FumeCryptoError} if `R` or `nonce` are the wrong length, or if
 * `ttlSeconds` is not an integer in `1 .. 2**32 - 1`.
 */
export function deriveSelector(R, nonce, ttlSeconds) {
    requireLength(R, SESSION_ROOT_SIZE, 'R');
    requireLength(nonce, SELECTOR_NONCE_SIZE, 'nonce');
    if (!Number.isInteger(ttlSeconds) || ttlSeconds < 1 || ttlSeconds > MAX_TTL_SECONDS) {
        throw new FumeCryptoError(`ttlSeconds must be an integer in 1..${MAX_TTL_SECONDS}, got ${ttlSeconds}`);
    }
    const preimage = concatBytes(utf8.encode(SELECTOR_DOMAIN), uint64be(R.length), R, uint64be(nonce.length), nonce, uint64be(8), uint64be(ttlSeconds));
    return sha256(preimage);
}
//# sourceMappingURL=selector.js.map